import React, { useState, useRef } from "react";
import axios from "axios";
import { Truck, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { API } from "@/utils/constants";
import { useT } from "@/i18n/LanguageContext";

export const LoginPage = ({ onLogin, onRequestAccess }) => {
  const [pin, setPin] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isSubmittingRef = useRef(false);
  const { t } = useT();

  const handleLogin = async (pinToSubmit) => {
    const fullPin = pinToSubmit || pin.join("");
    if (fullPin.length !== 4) {
      setError("Please enter a 4-digit PIN");
      return;
    }

    // Prevent double submission
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/auth/login?pin=${fullPin}`);
      onLogin(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid PIN");
      setPin(["", "", "", ""]);
      document.getElementById("pin-0")?.focus();
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const handlePinChange = (index, value) => {
    if (value.length > 1) return;
    if (value && !/^\d$/.test(value)) return;
    
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError("");
    
    // Auto-focus next input
    if (value && index < 3) {
      document.getElementById(`pin-${index + 1}`)?.focus();
    }
    
    // Auto-submit when all 4 digits are entered
    if (value && index === 3 && newPin.every(d => d)) {
      handleLogin(newPin.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      document.getElementById(`pin-${index - 1}`)?.focus();
    }
    // Allow Enter key to submit
    if (e.key === "Enter" && pin.every(d => d)) {
      handleLogin();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").slice(0, 4);
    if (/^\d{1,4}$/.test(pasted)) {
      const newPin = [...pin];
      for (let i = 0; i < pasted.length; i++) {
        newPin[i] = pasted[i];
      }
      setPin(newPin);
      if (pasted.length === 4) {
        document.getElementById("pin-3")?.focus();
        // Auto-submit on paste of full PIN
        handleLogin(newPin.join(""));
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo & Branding */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_vehicle-tracker-162/artifacts/n3an3dwp_djlj7u57_PEW.jpeg" 
              alt="Pacific Expressway" 
              className="h-20 w-auto mx-auto mb-4 object-contain"
            />
            <h1 className="text-2xl font-bold text-slate-900">Pacific Expressway</h1>
            <p className="text-slate-500 mt-1">{t("Fleet Maintenance")}</p>
          </div>

          {/* PIN Input */}
          <div className="mb-6">
            <Label className="text-sm font-medium text-slate-700 mb-3 block">{t("Enter your 4-digit PIN")}</Label>
            <div className="flex justify-center gap-3">
              {pin.map((digit, index) => (
                <input
                  key={index}
                  id={`pin-${index}`}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="w-14 h-14 text-center text-2xl font-bold border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                  data-testid={`pin-input-${index}`}
                />
              ))}
            </div>
            {error && (
              <p className="text-red-500 text-sm text-center mt-3">{error}</p>
            )}
          </div>

          {/* Sign In Button */}
          <Button 
            onClick={() => handleLogin()} 
            disabled={loading || pin.some(d => !d)}
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-lg font-medium"
            data-testid="btn-sign-in"
          >
            {loading ? t("Signing in...") : t("Sign In")}
          </Button>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-slate-500">{t("Don't have an account?")}</span>
            </div>
          </div>

          {/* Request Access Button */}
          <Button 
            variant="outline" 
            onClick={onRequestAccess}
            className="w-full h-12 border-blue-500 text-blue-600 hover:bg-blue-50"
            data-testid="btn-request-access"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            {t("Request Access")}
          </Button>
        </div>

        <p className="text-center text-slate-400 text-sm mt-6">
          Pacific Expressway © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
};
